import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { makeApi, type AdminProject } from '../api/client';

export function AdminProjectsPage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [projectReferenceNumber, setProjectReferenceNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [mainAddress, setMainAddress] = useState('');
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const r = await api.adminListProjects();
      setProjects(r.projects);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.adminCreateProject({ projectReferenceNumber, displayName, mainAddress });
      setProjectReferenceNumber('');
      setDisplayName('');
      setMainAddress('');
      setShowNew(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-400">{projects.length} projects</div>
        <button className="btn-primary" onClick={() => setShowNew(!showNew)}>
          {showNew ? 'Cancel' : 'New project'}
        </button>
      </div>

      {showNew && (
        <form onSubmit={onCreate} className="card grid gap-3 sm:grid-cols-3">
          <div>
            <label className="label flex items-center gap-1">
              Project reference number
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-700 text-gray-300 text-[10px] font-bold cursor-default"
                title="A short, unique code you assign (e.g. TARANTO-1). This is shared with residents so they can sign up — it cannot be changed after creation."
              >
                i
              </span>
            </label>
            <input
              className="input"
              value={projectReferenceNumber}
              onChange={(e) => setProjectReferenceNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
              placeholder="e.g. TARANTO-1"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Shared with residents for sign-up. Uppercase letters, numbers, hyphens only. Cannot be changed later.
            </p>
          </div>
          <div>
            <label className="label flex items-center gap-1">
              Display name
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-700 text-gray-300 text-[10px] font-bold cursor-default"
                title="The name residents will see when they pick this project during sign-up (e.g. 'Taranto Rd Marsfield'). Can be edited later."
              >
                i
              </span>
            </label>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Taranto Rd Marsfield"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown to residents. Keep it recognisable — building name or street works well.
            </p>
          </div>
          <div>
            <label className="label flex items-center gap-1">
              Main address
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-700 text-gray-300 text-[10px] font-bold cursor-default"
                title="The building's street address shown to residents on the sign-up address picker (e.g. '7-15 Taranto Rd Marsfield NSW 2122'). Can be edited later."
              >
                i
              </span>
            </label>
            <input
              className="input"
              value={mainAddress}
              onChange={(e) => setMainAddress(e.target.value)}
              placeholder="e.g. 7-15 Taranto Rd Marsfield NSW 2122"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Displayed as context on the sign-up page. Use the full street address including suburb, state and postcode.
            </p>
          </div>
          <div className="sm:col-span-3">
            <button className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {error && <div className="text-sm text-red-400">{error}</div>}
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto card p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/60 text-left text-gray-400 border-b border-gray-700">
              <tr>
                <th className="p-3">Project reference number</th>
                <th className="p-3">Display name</th>
                <th className="p-3">Main address</th>
                <th className="p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.pid} className="border-t border-gray-800 hover:bg-gray-800/40 transition-colors">
                  <td className="p-3">
                    <Link to={`/admin/projects/${p.pid}`} className="text-red-400 hover:text-red-300 hover:underline font-medium">
                      {p.projectReferenceNumber}
                    </Link>
                  </td>
                  <td className="p-3 text-gray-200">{p.displayName}</td>
                  <td className="p-3 text-gray-300">{p.mainAddress}</td>
                  <td className="p-3 text-xs text-gray-500">
                    {new Date(p.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-gray-500">
                    No projects
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
